import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api.js';
import { useAuth } from '../auth.jsx';
import { useI18n } from '../i18n/index.jsx';
import { formatPrice } from '../format.js';
import { UNITS, INCOTERMS } from '../productOptions.js';
import Icon from '../components/Icon.jsx';
import { useChat, useChatEvents, usePresence } from './ChatProvider.jsx';
import { StatusText } from './Presence.jsx';
import { Avatar, convTitle } from './ConversationList.jsx';
import { Linkified, timeOf, dayLabel, fileSize, fileIcon, useAuthBlob, downloadPrivate, EMOJIS } from './chatUtils.jsx';

// One conversation: header (back on phones, name, product chip, actions),
// product context card, messages (mine right, theirs left, date
// separators, seen ticks, typing indicator), and the composer (Enter to
// send, Shift+Enter new line, emoji, files, share a product, send a quote).
// base = '/chat' (member) or '/admin/chat' (team / moderation).
const MAX = 4000;
const MAX_FILE = 10 * 1024 * 1024;
const ACCEPT = '.jpg,.jpeg,.png,.webp,.gif,.pdf,.doc,.docx,.xls,.xlsx';

// May create documents from a conversation (rights of company users, migration 030).
const canDocs = (user) => {
  const tm = user?.team;
  if (!tm) return false;
  if (tm.isOwner || user.role === 'admin') return true;
  return !!tm.perms?.['docs.create'] && !!(tm.perms['customers.viewAll'] || tm.perms['customers.viewAssigned']);
};

export default function ChatView({ convId, base = '/chat', team = false, backTo, onChanged }) {
  const { t, lang, errorText } = useI18n();
  const { user } = useAuth();
  const { emitTyping, setUnread } = useChat();
  const [data, setData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reads, setReads] = useState({});
  const [more, setMore] = useState(false);
  const [error, setError] = useState('');
  const [typing, setTyping] = useState(null);
  const [menu, setMenu] = useState(false);
  const [modal, setModal] = useState(null); // 'product' | 'quote' | 'report' | { report: message }
  const scroller = useRef(null);
  const atBottom = useRef(true);
  const typingTimer = useRef(null);

  const conv = data?.conversation;
  const myKey = team ? 0 : user.id;
  const isMine = useCallback((m) => (team ? m.sender.role === 'support' : m.sender.role === 'member' && m.sender.id === user.id), [team, user.id]);
  const otherKey = conv ? (conv.kind === 'support' ? (team ? conv.other?.id : 0) : conv.other?.id) : null;
  const otherRead = reads[otherKey]?.lastReadId || 0;
  const presence = usePresence(conv?.other?.id ? [conv.other.id] : []);
  const status = conv?.other?.id ? presence[conv.other.id] : null;

  const load = useCallback(async () => {
    setError('');
    try {
      const d = await api(`${base}/conversations/${convId}`);
      setData(d); setMessages(d.messages); setMore(d.more); setReads(d.reads || {});
      atBottom.current = true;
    } catch (e) { setError(errorText(e)); setData(null); }
  }, [base, convId, errorText]);
  useEffect(() => { setData(null); setMessages([]); setTyping(null); load(); }, [load]);

  // Mark read when the newest message isn't mine and the tab is visible.
  const lastId = messages.length ? messages[messages.length - 1].id : 0;
  useEffect(() => {
    if (!data || !lastId || data.mode === 'moderation' || document.hidden) return undefined;
    const tm = setTimeout(() => {
      api(`${base}/conversations/${convId}/read`, { method: 'POST', body: { lastId } }).then((r) => { if (r.unread !== undefined) setUnread(r.unread); onChanged?.(); }).catch(() => {});
    }, 400);
    return () => clearTimeout(tm);
  }, [lastId, data, base, convId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const vis = () => { if (!document.hidden && lastId && data && data.mode !== 'moderation') api(`${base}/conversations/${convId}/read`, { method: 'POST', body: { lastId } }).catch(() => {}); };
    document.addEventListener('visibilitychange', vis);
    return () => document.removeEventListener('visibilitychange', vis);
  }, [lastId, data, base, convId]);

  useChatEvents((type, p) => {
    if (String(p.conversationId) !== String(convId)) return;
    if (type === 'message') {
      setMessages((list) => (list.some((m) => m.id === p.message.id) ? list : [...list, p.message]));
      if (!isMine(p.message)) setTyping(null);
    }
    if (type === 'read') setReads((r) => ({ ...r, [p.reader]: { lastReadId: Math.max(p.lastReadId, r[p.reader]?.lastReadId || 0) } }));
    if (type === 'typing' && p.userId !== myKey) {
      setTyping(p.name);
      clearTimeout(typingTimer.current);
      typingTimer.current = setTimeout(() => setTyping(null), 5000);
    }
  });

  // Keep the view at the bottom when new messages arrive (unless the user
  // scrolled up to read older ones).
  useLayoutEffect(() => {
    const el = scroller.current;
    if (el && atBottom.current) el.scrollTop = el.scrollHeight;
  }, [messages, typing]);
  const onScroll = () => { const el = scroller.current; atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80; };
  async function older() {
    const el = scroller.current; const h = el.scrollHeight;
    const d = await api(`${base}/conversations/${convId}?before=${messages[0].id}`);
    setMessages((list) => [...d.messages, ...list]); setMore(d.more);
    requestAnimationFrame(() => { el.scrollTop = el.scrollHeight - h; });
  }

  async function send(payload) {
    setError('');
    try {
      const m = await api(`${base}/conversations/${convId}/messages`, { method: 'POST', body: payload });
      atBottom.current = true;
      setMessages((list) => (list.some((x) => x.id === m.id) ? list : [...list, m]));
      onChanged?.();
      return true;
    } catch (e) { setError(errorText(e)); return false; }
  }
  async function sendFiles(files, body) {
    setError('');
    const bad = files.find((f) => f.size > MAX_FILE);
    if (bad) { setError(t('chat.fileTooBig', { name: bad.name })); return false; }
    const fd = new FormData();
    if (body) fd.append('body', body);
    for (const f of files) fd.append('files', f, f.name);
    try {
      const list = await api(`${base}/conversations/${convId}/attachments`, { method: 'POST', form: fd });
      atBottom.current = true;
      setMessages((cur) => [...cur, ...list.filter((m) => !cur.some((x) => x.id === m.id))]);
      onChanged?.();
      return true;
    } catch (e) { setError(errorText(e)); return false; }
  }
  async function settings(body, msg) {
    try { await api(`/chat/conversations/${convId}/settings`, { method: 'POST', body }); setMenu(false); onChanged?.(msg); await load(); } catch (e) { setError(errorText(e)); }
  }
  async function block(blocked) {
    try { await api('/chat/block', { method: 'POST', body: { userId: conv.other.id, blocked } }); setMenu(false); await load(); onChanged?.(); } catch (e) { setError(errorText(e)); }
  }

  // Messages grouped by day.
  const rows = useMemo(() => {
    const out = []; let day = '';
    for (const m of messages) {
      const d = new Date(m.createdAt).toDateString();
      if (d !== day) { out.push({ sep: dayLabel(m.createdAt, lang, t), key: `d${m.id}` }); day = d; }
      out.push({ m, key: m.id });
    }
    return out;
  }, [messages, lang, t]);

  if (error && !data) return <div className="chat-view"><div className="chat-empty"><p>{error}</p>{backTo && <Link to={backTo}>← {t('chat.back')}</Link>}</div></div>;
  if (!data) return <div className="chat-view"><div className="chat-empty muted">{t('common.loading')}</div></div>;

  const support = conv.kind === 'support';
  const title = convTitle(conv, t, team);
  const readOnly = data.mode === 'moderation' || !data.canSend;
  const blockedNote = conv.blockedByMe ? t('chat.youBlocked') : conv.blockedMe ? t('chat.blockedYou') : (user.chatBlocked && !support ? t('chat.banned') : '');

  return (
    <div className="chat-view">
      <header className="chat-head">
        {backTo && <Link to={backTo} className="chat-back" aria-label={t('chat.back')}><Icon name="arrow" size={20} /></Link>}
        <Avatar person={conv.other} support={support && !team} size={40} status={status} />
        <div className="chat-head-main">
          <strong>{title}</strong>
          <span className="muted small">{typing ? t('chat.typing', { name: typing }) : support && !team ? t('chat.supportSub') : data.mode === 'moderation' ? t('chat.moderation')
            : <>{conv.other?.name || ''}{status && <> · <StatusText status={status} /></>}</>}</span>
        </div>
        <AssignControl conv={conv} base={base} team={team} onDone={() => { load(); onChanged?.('assigned'); }} />
        {conv.other?.slug && !support && <a className="chat-head-link small" href={`/sellers/${conv.other.slug}`} target="_blank" rel="noreferrer">{t('chat.profile')}</a>}
        {!team && !readOnly && (
          <div className="chat-menu">
            <button type="button" className="chat-icon-btn" aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu(!menu)} aria-label={t('chat.actions')}>⋯</button>
            {menu && (
              <div className="chat-menu-list" role="menu" onKeyDown={(e) => { if (e.key === 'Escape') setMenu(false); }}>
                <button type="button" role="menuitem" onClick={() => settings({ archived: !conv.archived }, conv.archived ? 'unarchived' : 'archived')}>{conv.archived ? t('chat.unarchive') : t('chat.archive')}</button>
                <button type="button" role="menuitem" onClick={() => settings({ muted: !conv.muted })}>{conv.muted ? t('chat.unmute') : t('chat.mute')}</button>
                {!support && <button type="button" role="menuitem" onClick={() => block(!conv.blockedByMe)}>{conv.blockedByMe ? t('chat.unblock') : t('chat.block')}</button>}
                {!support && <button type="button" role="menuitem" onClick={() => { setMenu(false); setModal({ report: null }); }}>{t('chat.report')}</button>}
                <button type="button" role="menuitem" className="is-danger" onClick={() => { setMenu(false); setModal('delete'); }}>{t('chat.deleteForMe')}</button>
              </div>
            )}
          </div>
        )}
      </header>

      {conv.listing && (
        <a className="chat-context" href={`/marketplace/${conv.listing.id}`} target="_blank" rel="noreferrer">
          {conv.listing.photo ? <img src={conv.listing.photo} alt="" /> : <span className="chat-context-noimg"><Icon name="box" size={20} /></span>}
          <span><span className="muted small">{t('chat.about')}</span><strong>{conv.listing.title}</strong><span className="small">{formatPrice(conv.listing, lang, t)}</span></span>
        </a>
      )}
      {data.mode === 'moderation' && <p className="chat-note chat-note-warn">{t('chat.moderationNote')}</p>}

      <div className="chat-scroll" ref={scroller} onScroll={onScroll} aria-live="polite">
        {more && <button type="button" className="btn btn-link btn-sm chat-older" onClick={older}>{t('chat.older')}</button>}
        {!messages.length && <p className="chat-start muted small">{support && !team ? t('chat.supportStart') : t('chat.start')}</p>}
        {rows.map((r) => (r.sep
          ? <div key={r.key} className="chat-day"><span>{r.sep}</span></div>
          : <Bubble key={r.key} m={r.m} mine={isMine(r.m)} seen={isMine(r.m) && r.m.id <= otherRead} base={base} team={team} lang={lang} personId={user.person?.id}
            onReport={!team && !readOnly && !isMine(r.m) && r.m.sender.role === 'member' ? () => setModal({ report: r.m }) : null} />))}
        {typing && <div className="chat-typing" aria-label={t('chat.typing', { name: typing })}><span /><span /><span /></div>}
      </div>

      {error && <p className="chat-note chat-note-error" role="alert">{error}</p>}
      {readOnly ? <p className="chat-note">{data.mode === 'moderation' ? t('chat.readOnlyModeration') : t('chat.readOnlyMemberMode')}</p>
        : blockedNote ? <p className="chat-note">{blockedNote}{conv.blockedByMe && <> <button type="button" className="btn btn-link btn-sm" onClick={() => block(false)}>{t('chat.unblock')}</button></>}</p>
          : <Composer team={team} onSend={(body) => send({ kind: 'text', body })} onFiles={sendFiles} onTyping={() => emitTyping(Number(convId), team)}
            onProduct={!team ? () => setModal('product') : null} onQuote={() => setModal('quote')}
            docLink={(team ? support : !support) && canDocs(user) ? `${user.role === 'admin' ? '/admin/reps' : '/account/workspace'}/documents/new?convId=${conv.id}` : null} />}

      {modal === 'product' && <ProductPicker onClose={() => setModal(null)} onPick={async (id) => { if (await send({ kind: 'product', listingId: id })) setModal(null); }} />}
      {modal === 'quote' && <QuoteForm listing={conv.listing} onClose={() => setModal(null)} onSend={async (quote) => { if (await send({ kind: 'quote', quote, body: '' })) setModal(null); }} />}
      {modal?.report !== undefined && <ReportForm message={modal.report} onClose={() => setModal(null)}
        onSend={async (reason, note) => { await api('/chat/report', { method: 'POST', body: { conversationId: Number(convId), messageId: modal.report?.id, reason, note } }); setModal('reported'); }} />}
      {modal === 'reported' && <SmallModal title={t('chat.reportedTitle')} onClose={() => setModal(null)}><p>{t('chat.reportedText')}</p>
        {!conv.blockedByMe && !support && <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setModal(null); block(true); }}>{t('chat.blockToo')}</button>}</SmallModal>}
      {modal === 'delete' && (
        <SmallModal title={t('chat.deleteTitle')} onClose={() => setModal(null)}>
          <p>{t('chat.deleteText')}</p>
          <div className="editor-actions"><button type="button" className="btn btn-ghost" onClick={() => setModal(null)}>{t('common.cancel')}</button>
            <button type="button" className="btn btn-danger" onClick={() => { setModal(null); settings({ deleted: true }, 'deleted'); }}>{t('chat.deleteForMe')}</button></div>
        </SmallModal>
      )}
    </div>
  );
}

// "Assigné à": the company user (or the PME representative) handling the
// conversation. Shown to those who may hand it over.
function AssignControl({ conv, base, team, onDone }) {
  const { t, errorText } = useI18n();
  const { user } = useAuth();
  const [people, setPeople] = useState(null);
  const [error, setError] = useState('');
  const tm = user.team;
  const adminSupport = team && base === '/admin/chat' && conv.kind === 'support';
  const rep = base === '/workspace/chat';
  const company = !team && tm && !tm.pme && (tm.isOwner || tm.perms?.['chat.all'] || conv.assignedTo?.id === user.person?.id);
  const show = adminSupport || rep || company;
  useEffect(() => {
    if (!show) return;
    api('/team').then((d) => setPeople([...(adminSupport || rep ? [] : d.owners.map((o) => ({ id: o.id, name: o.name }))), ...d.members.filter((m) => m.status === 'active').map((m) => ({ id: m.userId, name: m.name }))]))
      .catch(() => setPeople([]));
  }, [show, adminSupport, rep]);
  if (!show || !people || !people.length) return null;
  async function change(v) {
    setError('');
    try { await api(`${base}/conversations/${conv.id}/assign`, { method: 'POST', body: { personId: v ? Number(v) : null } }); onDone(); } catch (e) { setError(errorText(e)); }
  }
  return (
    <label className="chat-assign small">
      <span>{t('chat.assignedTo')}</span>
      <select value={conv.assignedTo?.id || ''} onChange={(e) => change(e.target.value)}>
        <option value="">{adminSupport || rep ? t('chat.teamPme') : t('chat.nobody')}</option>
        {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      {error && <span className="field-error">{error}</span>}
    </label>
  );
}

function Bubble({ m, mine, seen, base, team, lang, onReport, personId }) {
  const { t } = useI18n();
  if (m.kind === 'system') return <div className="chat-system">{m.body}</div>;
  // Written by a team member / a PME representative: "Company – Name".
  const by = m.sender.by;
  const who = m.sender.role === 'support' ? (by ? `PME Trade Canada – ${by.name}` : t('chat.team')) : `${m.sender.company || m.sender.name}${by ? ` – ${by.name}` : ''}`;
  const showWho = !mine ? (team || m.sender.role === 'support' || !!by) : !!by && by.id !== personId;
  return (
    <div className={`chat-msg${mine ? ' is-mine' : ''}`}>
      <div className="chat-bubble">
        {showWho && <span className="chat-who">{by?.photoUrl && <img className="chat-who-photo" src={by.photoUrl} alt="" />}{mine ? `— ${by.name}` : who}{!mine && by?.title && <span className="chat-who-title"> · {by.title}</span>}</span>}
        {m.attachments.map((a) => <Attachment key={a.id} a={a} base={base} />)}
        {m.kind === 'product' && m.data && <ProductCard p={m.data} />}
        {m.kind === 'quote' && m.data && <QuoteCard q={m.data} lang={lang} />}
        {m.body && <p className="chat-text"><Linkified text={m.body} /></p>}
        <span className="chat-meta">
          {timeOf(m.createdAt, lang)}
          {mine && <span className={`chat-tick${seen ? ' is-seen' : ''}`} aria-label={seen ? t('chat.seen') : t('chat.sent')} title={seen ? t('chat.seen') : t('chat.sent')}>{seen ? '✓✓' : '✓'}</span>}
        </span>
      </div>
      {onReport && <button type="button" className="chat-msg-report" onClick={onReport} title={t('chat.reportMessage')} aria-label={t('chat.reportMessage')}>⚑</button>}
    </div>
  );
}

function Attachment({ a, base }) {
  const { t } = useI18n();
  const url = `/api${base}/attachments/${a.id}`;
  const src = useAuthBlob(a.isImage ? url : null);
  if (a.isImage) {
    return (
      <button type="button" className="chat-image" onClick={() => src && window.open(src, '_blank')} aria-label={t('chat.openImage', { name: a.name })}>
        {src ? <img src={src} alt={a.name} /> : <span className="chat-image-wait" />}
      </button>
    );
  }
  return (
    <button type="button" className="chat-file" onClick={() => downloadPrivate(url, a.name).catch(() => {})} title={t('chat.download')}>
      <span className={`chat-file-icon chat-file-${fileIcon(a.mime, a.name).toLowerCase()}`}>{fileIcon(a.mime, a.name)}</span>
      <span className="chat-file-main"><strong>{a.name}</strong><span className="small">{fileSize(a.size)} · {t('chat.download')}</span></span>
    </button>
  );
}

function ProductCard({ p }) {
  const { t, lang } = useI18n();
  return (
    <a className="chat-card chat-product" href={`/marketplace/${p.id}`} target="_blank" rel="noreferrer">
      {p.photo ? <img src={p.photo} alt="" /> : <span className="chat-context-noimg"><Icon name="box" size={22} /></span>}
      <span><span className="chat-card-kicker">{t('chat.sharedProduct')}</span><strong>{p.title}</strong><span className="small">{formatPrice(p, lang, t)}</span><span className="chat-card-link">{t('chat.viewProduct')} →</span></span>
    </a>
  );
}

function QuoteCard({ q, lang }) {
  const { t } = useI18n();
  const money = (n) => new Intl.NumberFormat(lang === 'en' ? 'en-CA' : 'fr-CA', { style: 'currency', currency: q.currency || 'USD' }).format(n);
  const unit = q.unit ? (t(`units.${q.unit}`).startsWith('units.') ? q.unit : t(`units.${q.unit}`)) : '';
  return (
    <div className="chat-card chat-quote">
      <span className="chat-card-kicker">🧾 {t('chat.quote')}</span>
      <strong>{q.product}</strong>
      <dl>
        <dt>{t('chat.q.quantity')}</dt><dd>{new Intl.NumberFormat(lang === 'en' ? 'en-CA' : 'fr-CA').format(q.quantity)} {unit}</dd>
        <dt>{t('chat.q.price')}</dt><dd>{money(q.price)}{unit ? ` / ${unit}` : ''}</dd>
        <dt>{t('chat.q.total')}</dt><dd><strong>{money(q.quantity * q.price)}</strong></dd>
        {q.incoterm && <><dt>Incoterm</dt><dd>{q.incoterm}</dd></>}
        {q.validUntil && <><dt>{t('chat.q.validUntil')}</dt><dd>{new Date(`${q.validUntil}T12:00:00`).toLocaleDateString(lang === 'en' ? 'en-CA' : 'fr-CA', { day: 'numeric', month: 'long', year: 'numeric' })}</dd></>}
      </dl>
      {q.note && <p className="small">{q.note}</p>}
      {/* An offer on a buying request (migration 042). */}
      {q.requestId && <a className="chat-card-link" href={`/marketplace/requests/${q.requestId}`} target="_blank" rel="noreferrer">{t('buying.wanted')} #{q.requestId} →</a>}
    </div>
  );
}

function Composer({ team, onSend, onFiles, onTyping, onProduct, onQuote, docLink }) {
  const { t } = useI18n();
  const [text, setText] = useState('');
  const [files, setFiles] = useState([]);
  const [emoji, setEmoji] = useState(false);
  const [plus, setPlus] = useState(false);
  const [busy, setBusy] = useState(false);
  const ta = useRef(null);
  const fileRef = useRef(null);
  useLayoutEffect(() => { const el = ta.current; if (el) { el.style.height = 'auto'; el.style.height = `${Math.min(160, el.scrollHeight)}px`; } }, [text]);
  // Cleared at once (typing can go on while it sends); put back if it fails.
  async function submit() {
    if (busy) return;
    const body = text.trim();
    const sending = files;
    if (!body && !sending.length) return;
    if (body.length > MAX) return;
    setBusy(true); setText(''); setFiles([]);
    const ok = sending.length ? await onFiles(sending, body) : await onSend(body);
    setBusy(false);
    if (!ok) {
      setText((cur) => (cur ? `${body}\n${cur}` : body));
      setFiles((cur) => [...sending, ...cur].slice(0, 5));
    }
    ta.current?.focus();
  }
  const insert = (e) => {
    const el = ta.current; const s = el.selectionStart ?? text.length;
    setText(text.slice(0, s) + e + text.slice(el.selectionEnd ?? s));
    setEmoji(false);
    requestAnimationFrame(() => { el.focus(); el.selectionStart = el.selectionEnd = s + e.length; });
  };
  return (
    <div className="chat-composer" onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); setFiles((f) => [...f, ...Array.from(e.dataTransfer.files)].slice(0, 5)); }}>
      {files.length > 0 && (
        <ul className="chat-pending">
          {files.map((f, i) => <li key={i}><span>{f.name}</span><span className="muted small">{fileSize(f.size)}</span><button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} aria-label={t('chat.removeFile')}>×</button></li>)}
        </ul>
      )}
      <div className="chat-compose-row">
        <div className="chat-pop">
          <button type="button" className="chat-icon-btn" onClick={() => { setPlus(!plus); setEmoji(false); }} aria-expanded={plus} aria-label={t('chat.more')}><Icon name="plus" size={20} /></button>
          {plus && (
            <div className="chat-pop-list" role="menu">
              <button type="button" role="menuitem" onClick={() => { setPlus(false); fileRef.current?.click(); }}>📎 {t('chat.attach')}</button>
              {onProduct && <button type="button" role="menuitem" onClick={() => { setPlus(false); onProduct(); }}>🛍 {t('chat.shareProduct')}</button>}
              {/* A document (LOI, quotation…) for this customer, sent back into this conversation. */}
              {docLink && <Link role="menuitem" to={docLink} onClick={() => setPlus(false)}>📄 {t('chat.document')}</Link>}
              {!team && <button type="button" role="menuitem" onClick={() => { setPlus(false); onQuote(); }}>🧾 {t('chat.sendQuote')}</button>}
            </div>
          )}
        </div>
        <input ref={fileRef} type="file" multiple accept={ACCEPT} className="sr-only" onChange={(e) => { setFiles((f) => [...f, ...Array.from(e.target.files)].slice(0, 5)); e.target.value = ''; }} />
        <label className="sr-only" htmlFor="chat-input">{t('chat.write')}</label>
        <textarea id="chat-input" ref={ta} rows={1} value={text} maxLength={MAX} placeholder={t('chat.write')}
          onChange={(e) => { setText(e.target.value); onTyping(); }}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); submit(); } }} />
        <div className="chat-pop">
          <button type="button" className="chat-icon-btn" onClick={() => { setEmoji(!emoji); setPlus(false); }} aria-expanded={emoji} aria-label={t('chat.emoji')}>🙂</button>
          {emoji && <div className="chat-emoji" role="menu">{EMOJIS.map((e) => <button key={e} type="button" role="menuitem" onClick={() => insert(e)}>{e}</button>)}</div>}
        </div>
        <button type="button" className="chat-send" onClick={submit} disabled={busy || (!text.trim() && !files.length)} aria-label={t('chat.send')}><Icon name="arrow" size={20} /></button>
      </div>
      {text.length > MAX - 300 && <p className="chat-count small">{text.length} / {MAX}</p>}
    </div>
  );
}

function SmallModal({ title, onClose, children, wide }) {
  const { t } = useI18n();
  useEffect(() => { const k = (e) => { if (e.key === 'Escape') onClose(); }; document.addEventListener('keydown', k); return () => document.removeEventListener('keydown', k); }, [onClose]);
  return (
    <div className="modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`modal chat-modal${wide ? ' chat-modal-wide' : ''}`} role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal-head"><h2>{title}</h2><button type="button" className="btn btn-link" onClick={onClose} aria-label={t('common.close')}>✕</button></div>
        <div className="modal-body chat-modal-body">{children}</div>
      </div>
    </div>
  );
}

function ProductPicker({ onClose, onPick }) {
  const { t, lang } = useI18n();
  const [list, setList] = useState(null);
  useEffect(() => { api('/my/listings').then((l) => setList(l.filter((x) => x.status === 'approved' && !x.hidden))).catch(() => setList([])); }, []);
  return (
    <SmallModal title={t('chat.shareProduct')} onClose={onClose}>
      {!list ? <p className="muted">{t('common.loading')}</p> : !list.length ? <p className="muted">{t('chat.noLiveProducts')}</p> : (
        <ul className="chat-pick">
          {list.map((l) => (
            <li key={l.id}><button type="button" onClick={() => onPick(l.id)}>
              {l.photos[0] ? <img src={l.photos[0].url} alt="" /> : <span className="chat-context-noimg"><Icon name="box" size={18} /></span>}
              <span><strong>{l.title}</strong><span className="small muted">{formatPrice(l, lang, t)}</span></span>
            </button></li>
          ))}
        </ul>
      )}
    </SmallModal>
  );
}

function QuoteForm({ listing, onClose, onSend }) {
  const { t } = useI18n();
  const valid = new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10);
  const [q, setQ] = useState({ product: listing?.title || '', listingId: listing?.id || null, quantity: '', unit: listing?.priceUnit || 'tonne', price: listing?.price ?? '', currency: listing?.currency || 'USD', incoterm: 'FOB', validUntil: valid, note: '' });
  const [err, setErr] = useState('');
  const set = (k) => (e) => setQ({ ...q, [k]: e.target.value });
  const submit = (e) => {
    e.preventDefault();
    if (!q.product.trim() || !(Number(String(q.quantity).replace(',', '.')) > 0) || !(Number(String(q.price).replace(',', '.')) >= 0) || q.price === '') { setErr(t('errors.quote_incomplete')); return; }
    onSend(q);
  };
  return (
    <SmallModal title={t('chat.sendQuote')} onClose={onClose} wide>
      <form className="form" onSubmit={submit} noValidate>
        <label className="field"><span className="field-label">{t('chat.q.product')}</span><input value={q.product} onChange={set('product')} maxLength={160} required /></label>
        <div className="grid-form">
          <label className="field"><span className="field-label">{t('chat.q.quantity')}</span><input inputMode="decimal" value={q.quantity} onChange={set('quantity')} required /></label>
          <label className="field"><span className="field-label">{t('chat.q.unit')}</span><select value={q.unit} onChange={set('unit')}>{UNITS.map((u) => <option key={u} value={u}>{t(`units.${u}`)}</option>)}</select></label>
        </div>
        <div className="grid-form">
          <label className="field"><span className="field-label">{t('chat.q.unitPrice')}</span><input inputMode="decimal" value={q.price} onChange={set('price')} required /></label>
          <label className="field"><span className="field-label">{t('chat.q.currency')}</span><select value={q.currency} onChange={set('currency')}>{['USD', 'CAD', 'EUR'].map((c) => <option key={c}>{c}</option>)}</select></label>
        </div>
        <div className="grid-form">
          <label className="field"><span className="field-label">Incoterm</span><select value={q.incoterm} onChange={set('incoterm')}><option value="">—</option>{INCOTERMS.map((c) => <option key={c}>{c}</option>)}</select></label>
          <label className="field"><span className="field-label">{t('chat.q.validUntil')}</span><input type="date" value={q.validUntil} onChange={set('validUntil')} /></label>
        </div>
        <label className="field"><span className="field-label">{t('chat.q.note')} <span className="field-optional">({t('common.optional')})</span></span><textarea rows={2} value={q.note} onChange={set('note')} maxLength={500} /></label>
        {err && <p className="field-error" role="alert">{err}</p>}
        <div className="editor-actions"><button type="button" className="btn btn-ghost" onClick={onClose}>{t('common.cancel')}</button><button type="submit" className="btn btn-primary">{t('chat.sendQuote')}</button></div>
      </form>
    </SmallModal>
  );
}

function ReportForm({ message, onClose, onSend }) {
  const { t, errorText } = useI18n();
  const [reason, setReason] = useState('spam');
  const [note, setNote] = useState('');
  const [err, setErr] = useState('');
  return (
    <SmallModal title={message ? t('chat.reportMessage') : t('chat.report')} onClose={onClose}>
      {message?.body && <blockquote className="chat-report-quote">{message.body.slice(0, 300)}</blockquote>}
      <form className="form" onSubmit={async (e) => { e.preventDefault(); try { await onSend(reason, note); } catch (x) { setErr(errorText(x)); } }}>
        <fieldset className="chat-reasons"><legend className="field-label">{t('chat.reportReason')}</legend>
          {['spam', 'scam', 'abuse', 'inappropriate', 'other'].map((r) => <label key={r} className="checkbox"><input type="radio" name="reason" checked={reason === r} onChange={() => setReason(r)} /><span>{t(`chat.reasons.${r}`)}</span></label>)}
        </fieldset>
        <label className="field"><span className="field-label">{t('chat.reportNote')} <span className="field-optional">({t('common.optional')})</span></span><textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} /></label>
        {err && <p className="field-error">{err}</p>}
        <div className="editor-actions"><button type="button" className="btn btn-ghost" onClick={onClose}>{t('common.cancel')}</button><button type="submit" className="btn btn-danger">{t('chat.reportSend')}</button></div>
      </form>
    </SmallModal>
  );
}
